import React, { useState } from 'react';
import styled from 'styled-components';
import { FiClock, FiMapPin } from 'react-icons/fi';

// Transport type options
const TRANSPORT_OPTIONS = [
  { id: 'train', icon: '🚆', label: 'Train', color: '#3f51b5' },
  { id: 'bus', icon: '🚌', label: 'Bus', color: '#9c27b0' },
  { id: 'car', icon: '🚗', label: 'Car', color: '#f44336' }
];

// Default transport durations (in minutes) between major cities
const DEFAULT_DURATIONS = {
  'lisbon-porto': { train: 180, bus: 210, car: 180 },
  'lisbon-sintra': { train: 40, bus: 45, car: 30 },
  'lisbon-algarve': { train: 180, bus: 210, car: 180 },
  'porto-douro': { train: 90, bus: 120, car: 90 },
  'porto-coimbra': { train: 60, bus: 90, car: 60 },
  'lisbon-coimbra': { train: 120, bus: 150, car: 130 },
  'porto-algarve': { train: 300, bus: 360, car: 300 },
  'sintra-porto': { train: 210, bus: 240, car: 210 },
  'algarve-porto': { train: 300, bus: 360, car: 300 },
  'algarve-lisbon': { train: 180, bus: 210, car: 180 },
  'coimbra-lisbon': { train: 120, bus: 150, car: 130 },
  'coimbra-porto': { train: 60, bus: 90, car: 60 },
  'sintra-lisbon': { train: 40, bus: 45, car: 30 },
  'lisbon-douro': { train: 240, bus: 300, car: 240 },
  'porto-sintra': { train: 210, bus: 240, car: 210 },
  'douro-porto': { train: 90, bus: 120, car: 90 },
  'coimbra-algarve': { train: 240, bus: 300, car: 240 },
  'algarve-coimbra': { train: 240, bus: 300, car: 240 },
  'sintra-algarve': { train: 210, bus: 240, car: 210 },
  'algarve-sintra': { train: 210, bus: 240, car: 210 },
  'douro-lisbon': { train: 240, bus: 300, car: 240 },
  'sintra-douro': { train: 270, bus: 330, car: 270 },
  'douro-sintra': { train: 270, bus: 330, car: 270 },
  'coimbra-douro': { train: 150, bus: 180, car: 150 },
  'douro-coimbra': { train: 150, bus: 180, car: 150 },
  'coimbra-sintra': { train: 150, bus: 180, car: 150 },
  'sintra-coimbra': { train: 150, bus: 180, car: 150 }
};

// Default transport prices (in EUR) between major cities
const DEFAULT_PRICES = {
  'lisbon-porto': { train: 25, bus: 15, car: 35 },
  'lisbon-sintra': { train: 5, bus: 4, car: 8 },
  'lisbon-algarve': { train: 30, bus: 20, car: 40 },
  'porto-douro': { train: 12, bus: 8, car: 15 },
  'porto-coimbra': { train: 10, bus: 7, car: 12 },
  'lisbon-coimbra': { train: 15, bus: 10, car: 20 },
  'porto-algarve': { train: 35, bus: 25, car: 45 },
  'sintra-porto': { train: 28, bus: 18, car: 38 },
  'algarve-porto': { train: 35, bus: 25, car: 45 },
  'algarve-lisbon': { train: 30, bus: 20, car: 40 },
  'coimbra-lisbon': { train: 15, bus: 10, car: 20 },
  'coimbra-porto': { train: 10, bus: 7, car: 12 },
  'sintra-lisbon': { train: 5, bus: 4, car: 8 },
  'lisbon-douro': { train: 30, bus: 20, car: 40 },
  'porto-sintra': { train: 28, bus: 18, car: 38 },
  'douro-porto': { train: 12, bus: 8, car: 15 },
  'coimbra-algarve': { train: 32, bus: 22, car: 42 },
  'algarve-coimbra': { train: 32, bus: 22, car: 42 },
  'sintra-algarve': { train: 32, bus: 22, car: 42 },
  'algarve-sintra': { train: 32, bus: 22, car: 42 },
  'douro-lisbon': { train: 30, bus: 20, car: 40 },
  'sintra-douro': { train: 32, bus: 22, car: 42 },
  'douro-sintra': { train: 32, bus: 22, car: 42 },
  'coimbra-douro': { train: 20, bus: 15, car: 25 },
  'douro-coimbra': { train: 20, bus: 15, car: 25 },
  'coimbra-sintra': { train: 18, bus: 12, car: 22 },
  'sintra-coimbra': { train: 18, bus: 12, car: 22 }
};

const TransportCard = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  margin: 1rem 0;
  position: relative;
  border-left: 4px solid #0a84ff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const TransportHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const RouteInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
  color: #333;
  font-weight: 500;
`;

const TransportOptions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin: 0.75rem 0;
`;

const TransportOption = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid #ddd;
  border-radius: 20px;
  background: ${props => props.$isSelected ? '#f0f7ff' : '#fff'};
  color: ${props => props.$isSelected ? props.$color : '#333'};
  border-color: ${props => props.$isSelected ? props.$color : '#ddd'};
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s;
  
  &:hover {
    background: #f0f7ff;
    border-color: ${props => props.$color};
  }
`;

const DurationInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: #666;
  margin-top: 0.5rem;
`;

const TransportSegment = ({ from, to, type = 'train' }) => {
  const [transportType, setTransportType] = useState(type);
  
  // Generate route key (e.g., 'lisbon-porto' or 'porto-lisbon')
  const routeKey = [from?.toLowerCase(), to?.toLowerCase()]
    .sort()
    .join('-')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
  
  // Get duration and price based on the route and transport type
  const getTransportInfo = (type) => {
    const duration = DEFAULT_DURATIONS[routeKey]?.[type] || 
                   (type === 'train' ? 120 : type === 'bus' ? 150 : 180);
    
    const price = DEFAULT_PRICES[routeKey]?.[type] || 
                 (type === 'train' ? 20 : type === 'bus' ? 15 : 30);
    
    return {
      duration: `${Math.floor(duration / 60)}h${duration % 60 ? ` ${duration % 60}m` : ''}`,
      price: `€${price}`
    };
  };
  
  const transportInfo = getTransportInfo(transportType);
  
  return (
    <TransportCard>
      <TransportHeader>
        <RouteInfo>
          <FiMapPin size={16} />
          <span>{from} → {to}</span>
        </RouteInfo>
      </TransportHeader>
      
      <TransportOptions>
        {TRANSPORT_OPTIONS.map((option) => (
          <TransportOption
            key={option.id}
            $isSelected={transportType === option.id}
            $color={option.color}
            onClick={() => setTransportType(option.id)}
          >
            <span>{option.icon}</span>
            <span>{option.label}</span>
          </TransportOption>
        ))}
      </TransportOptions>
      
      <DurationInfo>
        <FiClock size={14} />
        <span>{transportInfo.duration} • {transportInfo.price}</span>
      </DurationInfo>
    </TransportCard>
  );
};

export default TransportSegment;
